import { useState, useEffect } from 'react';
import { Shield, Users, Package, AlertTriangle, CheckCircle, XCircle, ToggleLeft, ToggleRight, Clock, DollarSign, Flag, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { formatDistanceToNow } from 'date-fns';

export default function AdminDashboard() {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState({});
  const [kybList, setKybList] = useState([]);
  const [escrows, setEscrows] = useState([]);
  const [disputes, setDisputes] = useState([]);
  const [riskFlags, setRiskFlags] = useState([]);
  const [eligibleTrusted, setEligibleTrusted] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, k, e, d, r, t] = await Promise.all([
        api.get('/admin/dashboard'),
        api.get('/admin/kyb/pending'),
        api.get('/admin/escrow/funded'),
        api.get('/admin/disputes'),
        api.get('/admin/risk-flags'),
        api.get('/admin/sellers/eligible-trusted'),
      ]);
      setStats(s.data.stats || {});
      setKybList(k.data.sellers || []);
      setEscrows(e.data.orders || []);
      setDisputes(d.data.disputes || []);
      setRiskFlags(r.data.flagged || []);
      setEligibleTrusted(t.data.sellers || []);
    } catch (err) { toast.error('Failed to load admin data'); }
    finally { setLoading(false); }
  };

  const approveKYB = async (userId) => {
    try { await api.patch(`/admin/kyb/${userId}/approve`); toast.success('KYB Approved ✅'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const rejectKYB = async (userId) => {
    const reason = prompt('Rejection reason:');
    if (!reason) return;
    try { await api.patch(`/admin/kyb/${userId}/reject`, { reason }); toast.success('KYB Rejected'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const releaseEscrow = async (orderId) => {
    try { await api.post(`/admin/escrow/${orderId}/release`); toast.success('Funds released to seller 💸'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const trustedRelease = async (orderId) => {
    try { await api.post(`/admin/escrow/${orderId}/trusted-release`); toast.success('Trusted seller quick release done ⚡'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const refundBuyer = async (orderId) => {
    const reason = prompt('Refund reason:');
    if (!reason) return;
    try { await api.post(`/admin/escrow/${orderId}/refund`, { reason }); toast.success('Buyer refunded'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const toggleTrusted = async (userId, current) => {
    try {
      await api.patch(`/admin/sellers/${userId}/trusted`, { isTrusted: !current });
      toast.success(!current ? 'Seller marked TRUSTED ⭐' : 'Trusted status removed');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const resolveDispute = async (orderId, action) => {
    const resolution = prompt('Resolution notes:');
    if (!resolution) return;
    try {
      await api.patch(`/admin/disputes/${orderId}/resolve`, { resolution, action });
      toast.success('Dispute resolved');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'kyb', label: `KYB (${kybList.length})`, icon: Users },
    { id: 'escrow', label: `Escrow (${escrows.length})`, icon: DollarSign },
    { id: 'disputes', label: `Disputes (${disputes.length})`, icon: AlertTriangle },
    { id: 'risk', label: 'Risk Flags', icon: Flag },
    { id: 'trusted', label: 'Trusted Sellers', icon: CheckCircle },
    { id: 'cs_staff', label: 'CS Staff', icon: Users },
    { id: 'fiat', label: 'Fiat Withdrawals', icon: DollarSign },
    { id: 'commission', label: 'Commission', icon: DollarSign },
  ];

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mt-4 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg,#FF6B00,#F5A623)' }}>
          <Shield size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Sora' }}>Admin Panel</h1>
          <p className="text-xs text-brand-muted">CryptoMart Control Center</p>
        </div>
      </div>

      {/* Tab nav */}
      <div className="flex gap-1 overflow-x-auto pb-2 mb-6" style={{ scrollbarWidth: 'none' }}>
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold flex-shrink-0 transition-all whitespace-nowrap"
            style={{
              background: tab === id ? 'linear-gradient(135deg,#FF6B00,#F5A623)' : '#12121A',
              color: tab === id ? 'white' : '#6B6B8A',
              border: tab === id ? 'none' : '1px solid #1E1E2E',
            }}>
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>

      {loading && <div className="flex justify-center py-10"><div className="spinner" /></div>}

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="space-y-4">
          {/* Platform Revenue shortcut */}
          <a href="/admin/revenue"
            className="flex items-center justify-between p-4 rounded-2xl transition-all hover:border-brand-orange"
            style={{ background:'linear-gradient(135deg,rgba(255,107,0,0.1),rgba(245,166,35,0.05))', border:'1px solid rgba(255,107,0,0.3)' }}>
            <div>
              <p className="font-bold text-brand-orange" style={{ fontFamily:'Sora' }}>💰 Platform Revenue Wallet</p>
              <p className="text-xs text-brand-muted mt-0.5">View earnings, withdraw to external wallet</p>
            </div>
            <span className="text-brand-orange text-lg">→</span>
          </a>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { label: 'Total Users', value: stats.totalUsers, color: '#4DA2FF' },
            { label: 'Total Sellers', value: stats.totalSellers, color: '#9945FF' },
            { label: 'Pending KYB', value: stats.pendingKYB, color: '#F5A623' },
            { label: 'Total Orders', value: stats.totalOrders, color: '#FF6B00' },
            { label: 'Funded Escrow', value: stats.pendingEscrow, color: '#00C864' },
            { label: 'Open Disputes', value: stats.disputedOrders, color: '#FF3232' },
            { label: 'Total Revenue', value: `$${(stats.totalRevenueUSD || 0).toFixed(0)}`, color: '#F7931A' },
            { label: 'Release Due Soon', value: stats.dueSoonForRelease, color: '#E040FB' },
          ].map(({ label, value, color }) => (
            <div key={label} className="p-4 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
              <p className="text-xs text-brand-muted mb-1">{label}</p>
              <p className="text-2xl font-bold" style={{ color, fontFamily: 'Sora' }}>{value ?? '—'}</p>
            </div>
          ))}
        </div>
        </div>
      )}

      {/* KYB REVIEW */}
      {tab === 'kyb' && (
        <div className="space-y-4">
          {kybList.length === 0 && <div className="text-center py-16 text-brand-muted">No pending KYB applications</div>}
          {kybList.map(seller => (
            <div key={seller._id} className="rounded-2xl p-4" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-bold">{seller.username}</p>
                  <p className="text-sm text-brand-muted">{seller.email}</p>
                  <p className="text-xs text-brand-muted mt-1">Business: <strong className="text-brand-text">{seller.kyb?.businessName}</strong> · {seller.kyb?.businessType} · {seller.kyb?.businessCountry}</p>
                  <p className="text-xs text-brand-muted mt-0.5">Submitted: {seller.kyb?.submittedAt ? formatDistanceToNow(new Date(seller.kyb.submittedAt), { addSuffix: true }) : 'N/A'}</p>
                </div>
                <span className="badge badge-yellow flex-shrink-0">Pending</span>
              </div>

              {/* Video links */}
              <div className="flex gap-2 flex-wrap mb-3">
                {seller.kyb?.faceVideoUrl && (
                  <a href={seller.kyb.faceVideoUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ background: 'rgba(255,107,0,0.1)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' }}>
                    <Eye size={12} /> Watch Face Video
                  </a>
                )}
                {seller.kyb?.productVideoUrl && (
                  <a href={seller.kyb.productVideoUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ background: 'rgba(77,162,255,0.1)', color: '#4DA2FF', border: '1px solid rgba(77,162,255,0.3)' }}>
                    <Eye size={12} /> Watch Product Video
                  </a>
                )}
                {seller.kyb?.businessDocUrl && (
                  <a href={seller.kyb.businessDocUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs" style={{ background: '#1E1E2E', color: '#6B6B8A' }}>
                    <Eye size={12} /> View Doc
                  </a>
                )}
              </div>

              <div className="flex gap-2">
                <button onClick={() => approveKYB(seller._id)} className="btn-primary flex-1 py-2 text-sm flex items-center justify-center gap-1">
                  <CheckCircle size={14} /> Approve
                </button>
                <button onClick={() => rejectKYB(seller._id)}
                  className="flex-1 py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-1"
                  style={{ background: 'rgba(255,50,50,0.1)', color: '#FF3232', border: '1px solid rgba(255,50,50,0.3)' }}>
                  <XCircle size={14} /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ESCROW */}
      {tab === 'escrow' && (
        <div className="space-y-4">
          {escrows.length === 0 && <div className="text-center py-16 text-brand-muted">No funded escrows</div>}
          {escrows.map(order => {
            const overdue = new Date(order.autoReleaseAt) <= new Date();
            return (
              <div key={order._id} className="rounded-2xl p-4" style={{ background: '#12121A', border: `1px solid ${overdue ? 'rgba(255,50,50,0.4)' : '#1E1E2E'}` }}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono text-sm font-bold">#{order.orderNumber}</p>
                    <p className="text-xs text-brand-muted">Buyer: {order.buyer?.username} · Seller: {order.seller?.username}</p>
                    <p className="text-xs mt-1 font-mono" style={{ color: '#F7931A' }}>
                      {order.cryptoAmount?.toFixed(6)} {order.paymentCurrency} (${order.totalUSD?.toFixed(2)})
                    </p>
                  </div>
                  <div className="text-right">
                    {order.sellerIsTrusted && (
                      <span className="badge badge-green text-xs mb-1 block">⭐ Trusted</span>
                    )}
                    {overdue ? (
                      <span className="badge badge-red text-xs">OVERDUE</span>
                    ) : (
                      <span className="text-xs text-brand-muted">
                        Release: {formatDistanceToNow(new Date(order.autoReleaseAt), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-xs text-brand-muted mb-3">
                  <span>Delivery: {order.deliveryEstimate?.type?.replace('_', ' ')} · {order.deliveryEstimate?.minDays}–{order.deliveryEstimate?.maxDays} days</span>
                </div>

                <div className="flex gap-2 flex-wrap">
                  {/* Trusted seller quick switch */}
                  {order.sellerIsTrusted && (
                    <button onClick={() => trustedRelease(order._id)}
                      className="flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold"
                      style={{ background: 'rgba(0,200,100,0.1)', color: '#00C864', border: '1px solid rgba(0,200,100,0.3)' }}>
                      <ToggleRight size={14} /> Quick Release (Trusted)
                    </button>
                  )}
                  <button onClick={() => releaseEscrow(order._id)} className="btn-primary px-3 py-2 text-xs flex items-center gap-1">
                    <CheckCircle size={12} /> Release to Seller
                  </button>
                  <button onClick={() => refundBuyer(order._id)}
                    className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1"
                    style={{ background: 'rgba(255,50,50,0.1)', color: '#FF3232', border: '1px solid rgba(255,50,50,0.3)' }}>
                    <XCircle size={12} /> Refund Buyer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* DISPUTES */}
      {tab === 'disputes' && (
        <div className="space-y-4">
          {disputes.length === 0 && <div className="text-center py-16 text-brand-muted">No open disputes</div>}
          {disputes.map(order => (
            <div key={order._id} className="rounded-2xl p-4" style={{ background: '#12121A', border: '1px solid rgba(255,50,50,0.3)' }}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-mono text-sm font-bold">#{order.orderNumber}</p>
                  <p className="text-xs text-brand-muted">Buyer: {order.buyer?.username} vs Seller: {order.seller?.username}</p>
                  <p className="text-xs mt-1 text-red-300">Reason: {order.dispute?.reason}</p>
                  <p className="text-xs text-brand-muted">Raised: {order.dispute?.raisedAt ? formatDistanceToNow(new Date(order.dispute.raisedAt), { addSuffix: true }) : 'N/A'}</p>
                </div>
                <span className="badge badge-red flex-shrink-0">Disputed</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => resolveDispute(order._id, 'release_seller')} className="btn-primary flex-1 py-2 text-xs">
                  Release to Seller
                </button>
                <button onClick={() => resolveDispute(order._id, 'refund_buyer')}
                  className="flex-1 py-2 rounded-xl text-xs font-semibold"
                  style={{ background: 'rgba(255,50,50,0.1)', color: '#FF3232', border: '1px solid rgba(255,50,50,0.3)' }}>
                  Refund Buyer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* RISK FLAGS */}
      {tab === 'risk' && (
        <div className="space-y-4">
          {riskFlags.length === 0 && <div className="text-center py-16 text-brand-muted">No risk flags</div>}
          {riskFlags.map(seller => (
            <div key={seller._id} className="rounded-2xl p-4" style={{ background: '#12121A', border: '1px solid rgba(245,166,35,0.3)' }}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-bold">{seller.username}</p>
                  <p className="text-xs text-brand-muted">{seller.email}</p>
                </div>
                <span className="badge badge-yellow">{seller.kyb?.riskFlags?.length} flag(s)</span>
              </div>
              <div className="space-y-2">
                {seller.kyb?.riskFlags?.map((flag, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded-lg" style={{ background: '#0A0A0F' }}>
                    <div>
                      <p className="text-sm">{flag.flag}</p>
                      <p className="text-xs text-brand-muted">{flag.createdAt ? formatDistanceToNow(new Date(flag.createdAt), { addSuffix: true }) : ''}</p>
                    </div>
                    {!flag.isResolved && (
                      <button onClick={async () => {
                        await api.patch(`/admin/sellers/${seller._id}/risk-flag/${i}/resolve`);
                        toast.success('Flag resolved'); fetchAll();
                      }} className="text-xs px-2 py-1 rounded-lg" style={{ background: 'rgba(0,200,100,0.1)', color: '#00C864' }}>
                        Resolve
                      </button>
                    )}
                    {flag.isResolved && <span className="badge badge-green text-xs">Resolved</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TRUSTED SELLERS */}
      {tab === 'trusted' && (
        <div className="space-y-4">
          <div className="p-3 rounded-xl text-sm" style={{ background: 'rgba(255,107,0,0.06)', border: '1px solid rgba(255,107,0,0.2)', color: '#E0E0F0' }}>
            Sellers with 10+ completed trades and 10+ 4-5 star reviews are eligible. You decide who gets trusted status. This is completely hidden from sellers.
          </div>
          {eligibleTrusted.length === 0 && <div className="text-center py-12 text-brand-muted">No sellers eligible yet</div>}
          {eligibleTrusted.map(seller => (
            <div key={seller._id} className="flex items-center justify-between p-4 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
              <div>
                <p className="font-bold">{seller.username}</p>
                <p className="text-xs text-brand-muted">{seller.store?.name}</p>
                <div className="flex gap-3 mt-1 text-xs text-brand-muted">
                  <span>🤝 {seller.trustedSeller?.tradeCount} trades</span>
                  <span>⭐ {seller.trustedSeller?.goodReviewCount} good reviews</span>
                </div>
              </div>
              <button onClick={() => toggleTrusted(seller._id, false)}
                className="flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-semibold"
                style={{ background: 'rgba(0,200,100,0.1)', color: '#00C864', border: '1px solid rgba(0,200,100,0.3)' }}>
                <ToggleLeft size={16} /> Mark Trusted
              </button>
            </div>
          ))}
        </div>
      )}

      {/* CS STAFF MANAGEMENT */}
      {tab === 'cs_staff' && (
        <CSStaffPanel />
      )}

      {/* COMMISSION RATES MANAGEMENT */}
      {tab === 'commission' && (
        <CommissionAdminPanel />
      )}

      {/* FIAT WITHDRAWALS */}
      {tab === 'fiat' && (
        <FiatWithdrawalsPanel />
      )}
    </div>
  );
}

// ── CS Staff Panel ────────────────────────────────────────────
function CSStaffPanel() {
  const [staff, setStaff] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/support/admin/staff').then(r => setStaff(r.data.staff || [])).finally(() => setLoading(false));
  }, []);

  const findUser = async () => {
    try {
      // Search by username via users endpoint
      const res = await api.get(`/users/search?q=${searchUser}`);
      setSearchResult(res.data.users?.[0] || null);
      if (!res.data.users?.length) toast.error('User not found');
    } catch { toast.error('Search failed'); }
  };

  const promoteToCS = async (userId, username) => {
    try {
      await api.patch(`/support/admin/staff/${userId}/promote`);
      toast.success(`${username} is now CS Staff ✅`);
      const res = await api.get('/support/admin/staff');
      setStaff(res.data.staff || []);
      setSearchResult(null); setSearchUser('');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const demoteCS = async (userId, username) => {
    if (!confirm(`Remove ${username} from CS Staff?`)) return;
    try {
      await api.patch(`/support/admin/staff/${userId}/demote`);
      toast.success(`${username} removed from CS Staff`);
      setStaff(prev => prev.filter(s => s._id !== userId));
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 rounded-xl text-sm" style={{ background: 'rgba(77,162,255,0.06)', border: '1px solid rgba(77,162,255,0.2)', color: '#E0E0F0' }}>
        You can have <strong className="text-white">maximum 3 CS Staff</strong> accounts. CS staff can view and respond to support tickets, process fiat withdrawals, but cannot access KYB, escrow release or admin-only controls.
      </div>

      {/* Current CS staff */}
      <div>
        <h3 className="font-bold mb-3 text-sm" style={{ fontFamily: 'Sora' }}>
          Current CS Staff ({staff.length}/3)
        </h3>
        {loading ? <div className="spinner mx-auto" /> :
          staff.length === 0 ? <p className="text-brand-muted text-sm">No CS staff yet</p> :
          staff.map(s => (
            <div key={s._id} className="flex items-center justify-between p-3 rounded-xl mb-2" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
              <div>
                <p className="font-semibold text-sm">{s.username}</p>
                <p className="text-xs text-brand-muted">{s.email}</p>
              </div>
              <button onClick={() => demoteCS(s._id, s.username)}
                className="text-xs px-3 py-1.5 rounded-lg font-semibold"
                style={{ background: 'rgba(255,50,50,0.1)', color: '#FF3232', border: '1px solid rgba(255,50,50,0.3)' }}>
                Remove
              </button>
            </div>
          ))
        }
      </div>

      {/* Add new CS staff */}
      {staff.length < 3 && (
        <div className="glass rounded-2xl p-4">
          <h3 className="font-bold mb-3 text-sm" style={{ fontFamily: 'Sora' }}>Add CS Staff Account</h3>
          <p className="text-xs text-brand-muted mb-3">The person must already have a CryptoMart account. Search by their username.</p>
          <div className="flex gap-2">
            <input className="input-dark flex-1 text-sm" placeholder="Search username..."
              value={searchUser} onChange={e => setSearchUser(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && findUser()} />
            <button onClick={findUser} className="btn-outline px-4 text-sm">Search</button>
          </div>
          {searchResult && (
            <div className="flex items-center justify-between mt-3 p-3 rounded-xl" style={{ background: '#0A0A0F', border: '1px solid #1E1E2E' }}>
              <div>
                <p className="font-semibold text-sm">{searchResult.username}</p>
                <p className="text-xs text-brand-muted">{searchResult.email} · {searchResult.country}</p>
              </div>
              <button onClick={() => promoteToCS(searchResult._id, searchResult.username)} className="btn-primary px-3 py-1.5 text-xs">
                Make CS Staff
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Commission Admin Panel ────────────────────────────────────
function CommissionAdminPanel() {
  const [rates, setRates] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [premiumSubs, setPremiumSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    Promise.all([
      api.get('/commission/rates'),
      api.get('/commission/admin/revenue').catch(() => ({ data: {} })),
      api.get('/commission/admin/premium-subs').catch(() => ({ data: { subs: [] } })),
    ]).then(([r, rev, p]) => {
      setRates(r.data.rates || []);
      setRevenue(rev.data);
      setPremiumSubs(p.data.subs || []);
    }).finally(() => setLoading(false));
  }, []);

  const seedRates = async () => {
    try {
      await api.post('/commission/admin/seed');
      toast.success('Default rates seeded!');
      const r = await api.get('/commission/rates');
      setRates(r.data.rates || []);
    } catch { toast.error('Failed'); }
  };

  const saveRate = async () => {
    try {
      await api.patch(`/commission/admin/rates/${encodeURIComponent(editing)}`, editForm);
      toast.success('Rate updated');
      setEditing(null);
      const r = await api.get('/commission/rates');
      setRates(r.data.rates || []);
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-10"><div className="spinner" /></div>;

  return (
    <div className="space-y-4">
      {/* Revenue summary */}
      {revenue?.byCategory?.length > 0 && (
        <div className="p-4 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
          <h3 className="font-bold mb-3 text-sm" style={{ fontFamily: 'Sora' }}>Commission Revenue by Category</h3>
          {revenue.byCategory.map(c => (
            <div key={c._id} className="flex justify-between text-xs py-1.5" style={{ borderBottom: '1px solid #1E1E2E' }}>
              <span className="text-brand-muted">{c._id}</span>
              <div className="flex gap-4">
                <span>{c.totalOrders} orders</span>
                <span className="font-bold text-green-400">${c.totalRevenue?.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Premium subscriptions */}
      {premiumSubs.length > 0 && (
        <div className="p-4 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
          <h3 className="font-bold mb-3 text-sm" style={{ fontFamily: 'Sora' }}>Active Premium Subscriptions ({premiumSubs.length})</h3>
          {premiumSubs.map(sub => (
            <div key={sub._id} className="flex justify-between text-xs py-1.5" style={{ borderBottom: '1px solid #1E1E2E' }}>
              <span>{sub.seller?.username} · {sub.seller?.store?.name}</span>
              <div className="flex gap-3">
                <span className="capitalize font-bold" style={{ color: '#F5A623' }}>{sub.plan}</span>
                <span className="text-brand-muted">${sub.monthlyFeeUSD}/mo</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Rates management */}
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold text-sm" style={{ fontFamily: 'Sora' }}>Category Commission Rates</h3>
        <button onClick={seedRates} className="btn-outline py-1.5 px-3 text-xs">Reset to Defaults</button>
      </div>

      {rates.length === 0 && (
        <div className="text-center py-8">
          <p className="text-brand-muted text-sm mb-3">No rates found</p>
          <button onClick={seedRates} className="btn-primary px-6 text-sm">Seed Default Rates</button>
        </div>
      )}

      {rates.map(rate => (
        <div key={rate._id} className="p-3 rounded-xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
          {editing === rate.category ? (
            <div className="space-y-2">
              <p className="font-semibold text-sm">{rate.category}</p>
              <div className="grid grid-cols-4 gap-2">
                {[['defaultRate','Default %'],['minRate','Min %'],['maxRate','Max %'],['shippingCut','Ship %']].map(([key,label]) => (
                  <div key={key}>
                    <p className="text-xs text-brand-muted mb-1">{label}</p>
                    <input type="number" className="input-dark text-xs" value={editForm[key] || ''} min="0" max="50" step="0.5"
                      onChange={e => setEditForm({...editForm, [key]: parseFloat(e.target.value)})} />
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={saveRate} className="btn-primary flex-1 py-1.5 text-xs">Save</button>
                <button onClick={() => setEditing(null)} className="btn-outline flex-1 py-1.5 text-xs">Cancel</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">{rate.category}</p>
                <div className="flex gap-3 text-xs text-brand-muted mt-1">
                  <span>Default: <strong className="text-brand-orange">{rate.defaultRate}%</strong></span>
                  <span>Range: {rate.minRate}–{rate.maxRate}%</span>
                  <span>Ship: {rate.shippingCut}%</span>
                  <span className="text-green-400">Total: ~{rate.defaultRate + rate.shippingCut + 3}%</span>
                </div>
              </div>
              <button onClick={() => { setEditing(rate.category); setEditForm({ defaultRate: rate.defaultRate, minRate: rate.minRate, maxRate: rate.maxRate, shippingCut: rate.shippingCut }); }}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold"
                style={{ background: 'rgba(255,107,0,0.1)', color: '#FF6B00', border: '1px solid rgba(255,107,0,0.3)' }}>
                Edit
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
function FiatWithdrawalsPanel() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = async () => {
    setLoading(true);
    api.get('/fiat/withdrawal/all').then(r => setWithdrawals(r.data.withdrawals || [])).finally(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  const process = async (id) => {
    const ref = prompt('Enter bank transfer reference number:');
    if (!ref) return;
    try {
      await api.patch(`/fiat/withdrawal/${id}/process`, { bankReference: ref });
      toast.success('Withdrawal marked as completed ✅');
      fetch();
    } catch { toast.error('Failed'); }
  };

  const fail = async (id) => {
    const reason = prompt('Reason for failure:');
    if (!reason) return;
    try {
      await api.patch(`/fiat/withdrawal/${id}/fail`, { reason });
      toast.success('Marked as failed');
      fetch();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-3">
      {loading ? <div className="flex justify-center py-10"><div className="spinner" /></div> :
        withdrawals.length === 0 ? <div className="text-center py-16 text-brand-muted">No pending fiat withdrawals</div> :
        withdrawals.map(w => (
          <div key={w._id} className="p-4 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            <div className="flex justify-between mb-2">
              <div>
                <p className="font-bold text-sm">{w.seller?.username} · {w.seller?.store?.name}</p>
                <p className="text-xs text-brand-muted">{w.seller?.email}</p>
              </div>
              <span className="badge badge-yellow text-xs">Pending</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              <div className="p-2 rounded-lg" style={{ background: '#0A0A0F' }}>
                <p className="text-brand-muted">Crypto</p>
                <p className="font-bold font-mono">{w.amountCrypto} {w.cryptoCurrency}</p>
              </div>
              <div className="p-2 rounded-lg" style={{ background: '#0A0A0F' }}>
                <p className="text-brand-muted">Fiat</p>
                <p className="font-bold" style={{ color: '#00C864' }}>{w.fiatCurrency} {w.fiatAmount?.toLocaleString()}</p>
              </div>
              <div className="p-2 rounded-lg col-span-2" style={{ background: '#0A0A0F' }}>
                <p className="text-brand-muted">Bank Details</p>
                <p className="font-semibold">{w.bankDetails?.bankName} · {w.bankDetails?.accountNumber}</p>
                <p className="text-brand-muted">{w.bankDetails?.accountName}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => process(w._id)} className="btn-primary flex-1 py-2 text-xs">✅ Mark Completed</button>
              <button onClick={() => fail(w._id)}
                className="flex-1 py-2 rounded-xl text-xs font-semibold"
                style={{ background: 'rgba(255,50,50,0.1)', color: '#FF3232', border: '1px solid rgba(255,50,50,0.3)' }}>
                ❌ Mark Failed
              </button>
            </div>
          </div>
        ))
      }
    </div>
  );
}
